import React from 'react';
import { Button, Tooltip } from 'antd';
import styled from 'styled-components';

import { CheckOutlined, CloseOutlined } from '@ant-design/icons';

import { LanguageLevelEnum, LanguageType } from '../../common/types';
import { DisplaySetSuggestionItem, DisplaySetSuggestionList } from '../candidate-ai/candidateAiSuggestions.types';
import { CandidateAiReviewAction } from '../candidate-ai/useCandidateAiReviewActions';
import { CandidateType } from '../candidates/Candidates.types';

import { SuggestionIconButton, SuggestionReviewProps, SuggestionValueRow } from './CandidateAiScalarRows';
import { sectionAcceptAllKey } from './useCandidateCvSuggestionsReview';

// Wide gaps between sections and a tight heading keep each heading (and its action) with the rows below it.
export const SuggestionSection = styled.section`
  & + & {
    margin-top: 40px;
  }

  .cv-suggestions-section-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 2px;
    min-height: 32px;
  }
  .cv-suggestions-section-title {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #526174;
  }
`;

// Quiet heading-level actions: they belong to the heading line, not to the rows below it.
export const HeadingActionButton = styled(Button)<{ $tone?: 'accept' | 'neutral' }>`
  &.ant-btn {
    height: 26px;
    padding: 0 8px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    color: ${({ $tone = 'neutral' }) => ($tone === 'accept' ? '#166534' : '#526174')};
  }

  &.ant-btn:not(:disabled):hover {
    color: ${({ $tone = 'neutral' }) => ($tone === 'accept' ? '#14532d' : '#18283b')} !important;
    background: ${({ $tone = 'neutral' }) =>
      $tone === 'accept' ? 'rgba(34, 197, 94, 0.12)' : 'rgba(15, 23, 42, 0.06)'} !important;
  }
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid #e5eaf0;
`;

const SuggestionChip = styled.div<{ $rejected: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  border-radius: 6px;
  padding: 2px 2px 2px 10px;
  border: 1px solid #dce4ed;
  background: #ffffff;
  font-size: 14px;
  color: ${({ $rejected }) => ($rejected ? '#64748b' : '#18283b')};
`;

const isSameAction = (currentAction: CandidateAiReviewAction | null, expected: CandidateAiReviewAction) =>
  !!currentAction &&
  currentAction.field === expected.field &&
  currentAction.type === expected.type &&
  JSON.stringify('value' in currentAction ? currentAction.value : null) ===
    JSON.stringify('value' in expected ? expected.value : null);

const formatLevel = (level?: string | null) =>
  level ? LanguageLevelEnum[level as keyof typeof LanguageLevelEnum] ?? level : 'Not set';

type SuggestionListItemsProps = SuggestionReviewProps & {
  candidate: CandidateType;
  list: DisplaySetSuggestionList;
  // Rejected items can only be accepted again.
  rejected: boolean;
};

export const SuggestionListItems: React.FC<SuggestionListItemsProps> = ({
  candidate,
  list,
  rejected,
  actionsDisabled,
  isReviewLoading,
  activeReviewAction,
  onReview,
}) => {
  const actionsFor = (item: DisplaySetSuggestionItem) => {
    const accept: CandidateAiReviewAction = { field: list.field, type: 'ACCEPT_SET_ITEM', value: item.value };
    const reject: CandidateAiReviewAction = { field: list.field, type: 'REJECT_SET_ITEM', value: item.value };
    return {
      acceptLoading: isReviewLoading && isSameAction(activeReviewAction, accept),
      rejectLoading: isReviewLoading && isSameAction(activeReviewAction, reject),
      onAccept: () => onReview([accept], `set:${list.field}:accept:${item.displayValue}`),
      onReject: rejected ? undefined : () => onReview([reject], `set:${list.field}:reject:${item.displayValue}`),
    };
  };

  if (list.field === 'languages') {
    return (
      <>
        {list.items.map((item) => {
          const language = item.value as LanguageType;
          const current = candidate.languages?.find((known) => known.language === language.language);
          return (
            <SuggestionValueRow
              key={item.displayValue}
              label={language.language}
              currentValue={formatLevel(current?.level)}
              suggestedValue={formatLevel(language.level)}
              actionsDisabled={actionsDisabled}
              {...actionsFor(item)}
            />
          );
        })}
      </>
    );
  }

  return (
    <ChipRow>
      {list.items.map((item) => {
        const { acceptLoading, rejectLoading, onAccept, onReject } = actionsFor(item);
        return (
          <SuggestionChip key={item.displayValue} $rejected={rejected}>
            <span>{item.displayValue}</span>
            <Tooltip title="Accept">
              <SuggestionIconButton
                $tone="accept"
                type="text"
                size="small"
                aria-label={`Accept ${item.displayValue}`}
                icon={<CheckOutlined />}
                loading={acceptLoading}
                disabled={actionsDisabled}
                onClick={onAccept}
              />
            </Tooltip>
            {onReject && (
              <Tooltip title="Reject">
                <SuggestionIconButton
                  $tone="reject"
                  type="text"
                  size="small"
                  aria-label={`Reject ${item.displayValue}`}
                  icon={<CloseOutlined />}
                  loading={rejectLoading}
                  disabled={actionsDisabled}
                  onClick={onReject}
                />
              </Tooltip>
            )}
          </SuggestionChip>
        );
      })}
    </ChipRow>
  );
};

type CandidateAiSetSuggestionGroupProps = SuggestionReviewProps & {
  candidate: CandidateType;
  list: DisplaySetSuggestionList;
};

export const CandidateAiSetSuggestionGroup: React.FC<CandidateAiSetSuggestionGroupProps> = ({ list, ...props }) => {
  const acceptAllKey = sectionAcceptAllKey(list.field);

  return (
    <SuggestionSection role="group" aria-label={list.label}>
      <div className="cv-suggestions-section-heading">
        <h2 className="cv-suggestions-section-title">{`${list.label} · ${list.items.length}`}</h2>
        <HeadingActionButton
          $tone="accept"
          type="text"
          icon={<CheckOutlined aria-hidden />}
          loading={props.isReviewLoading && props.activeReviewKey === acceptAllKey}
          disabled={props.actionsDisabled}
          onClick={() =>
            props.onReview(
              list.items.map((item) => ({ field: list.field, type: 'ACCEPT_SET_ITEM' as const, value: item.value })),
              acceptAllKey
            )
          }
        >
          {`Accept all ${list.label.toLowerCase()}`}
        </HeadingActionButton>
      </div>
      <SuggestionListItems list={list} rejected={false} {...props} />
    </SuggestionSection>
  );
};
